// SkillTable — list view used on /skills (company + global scopes).
//
// `mode` controls per-row chrome:
//   "company" (default) — full row: version chip, state badge, Global pill,
//     trailing Edit chip; whole row is a <Link> to /skills/:id/edit.
//   "global" — starter-skill catalog: hide version chip / state badge /
//     Global pill, trailing button is either "Import to my Company"
//     (clones into the current company) or, if already cloned, a chip-
//     link to open the local copy. Row wrapper becomes a <div> so the
//     button doesn't fight a parent navigation.
import { Link, router } from "@inertiajs/react"
import { Link2 } from "lucide-react"
import { Badge, type BadgeTone } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { RowAvatar } from "./row-avatar"
import { fmtNumber, fmtRelativeTime } from "@/lib/format"
import type { Skill, SkillCategory, SkillState } from "@/types/skill"

type Props = {
  skills: Skill[]
  mode?: "company" | "global"
}

const CATEGORY_LABEL: Record<SkillCategory, string> = {
  knowledge_source: "Knowledge Source",
  response_action:  "Response Action",
  routing_logic:    "Routing Logic",
  workflow:         "Workflow",
  integration:      "Integration",
}

const STATUS_TONE: Record<SkillState, BadgeTone> = {
  live:     "gold",
  draft:    "neutral",
  archived: "red",
}

const STATUS_LABEL: Record<SkillState, string> = {
  live:     "Live",
  draft:    "Draft",
  archived: "Archived",
}

export function SkillTable({ skills, mode = "company" }: Props) {
  if (skills.length === 0) {
    return (
      <div className="rounded-card border border-border bg-app-panel p-6 text-sm text-app-muted">
        No skills in this view.
      </div>
    )
  }

  const isGlobalMode = mode === "global"

  return (
    <div className="overflow-hidden rounded-card border border-border bg-app-panel">
      <div className="divide-y divide-border">
        {skills.map((s) => {
          // Below md: stack into a single-column card (avatar+title row,
          // category/usage row, status/agent count + action button row).
          // From md+ the original 7-col grid layout takes over.
          const rowClass =
            "flex flex-col gap-3 px-4 py-4 text-sm transition outline-none hover:bg-white/5 focus-visible:bg-white/5 focus-visible:ring-1 focus-visible:ring-brand-red/40 " +
            "md:grid md:grid-cols-[auto_minmax(0,1fr)_140px_110px_90px_70px_auto] md:items-center md:gap-3 md:px-5 " +
            "xl:grid-cols-[auto_minmax(0,1fr)_160px_110px_90px_70px_auto] xl:gap-4"

          const innerCells = (
            <>
              {/* Mobile: title + chips header row (avatar inline with
                  name). Desktop: avatar takes its own grid column to
                  the left of the title block. */}
              <div className="flex items-start gap-3 md:contents">
                <div className="md:contents">
                  <RowAvatar name={s.name} />
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="truncate font-semibold text-white">{s.name}</span>
                    {!isGlobalMode && (
                      <>
                        <span
                          className={
                            s.live
                              ? "rounded-chip border border-vip-gold/30 bg-vip-goldMute px-1.5 py-0.5 text-[10px] font-medium text-vip-goldSoft"
                              : "rounded-chip border border-brand-red/30 bg-brand-redSoft px-1.5 py-0.5 text-[10px] font-medium text-brand-red"
                          }
                        >
                          {s.versionLabel}
                        </span>
                        {s.isGlobal && (
                          <span className="rounded-chip border border-border bg-white/5 px-1.5 py-0.5 text-[10px] font-medium text-app-muted">
                            Global
                          </span>
                        )}
                      </>
                    )}
                  </div>
                  <div className="truncate text-xs text-app-muted">
                    Used {fmtNumber(s.usageCount)} times · updated {fmtRelativeTime(s.updatedAt)}
                  </div>
                  {isGlobalMode && s.dependencyKeys && s.dependencyKeys.length > 0 && (
                    <div className="mt-1 flex items-center gap-1 truncate text-[11px] text-app-muted">
                      <Link2 size={11} className="shrink-0" />
                      <span className="truncate">Also imports: {s.dependencyKeys.join(", ")}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Mobile: meta row — category + agent count + success
                  rate + status badge wrap together. Desktop: each lives
                  in its own grid column. */}
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-xs md:contents md:text-sm">
                <span className="text-app-muted md:text-sm">
                  {CATEGORY_LABEL[s.category]}
                </span>

                <span className="md:block">
                  {!isGlobalMode && (
                    <Badge tone={STATUS_TONE[s.status]}>{STATUS_LABEL[s.status]}</Badge>
                  )}
                </span>

                <span className="text-app-muted md:block">
                  {s.agentIds.length} agent{s.agentIds.length === 1 ? "" : "s"}
                </span>

                <span className="font-semibold text-vip-goldSoft md:block md:text-right">
                  {s.successRate == null ? "—" : `${Math.round(s.successRate * 100)}%`}
                </span>
              </div>

              {/* Action cell. On mobile, stretches full-width below
                  the meta row; on desktop, snaps into the right grid
                  column with the page's normal width. */}
              <div className="flex justify-stretch md:justify-end">
              {isGlobalMode ? (
                s.clonedToCurrentCompanySkillId == null ? (
                  <Button
                    type="button"
                    variant="red"
                    size="sm"
                    className="w-full md:w-auto"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      router.post(`/skills/${s.id}/clone`, {}, {})
                    }}
                  >
                    Import to my Company
                  </Button>
                ) : (
                  <Link
                    href={`/skills/${s.clonedToCurrentCompanySkillId}/test`}
                    className="text-xs text-status-success hover:underline"
                  >
                    ✓ Already imported · Open
                  </Link>
                )
              ) : (
                <span className="flex w-full items-center gap-1.5 md:w-auto">
                  <span className="flex-1 rounded-control border border-border px-3 py-1.5 text-center text-xs font-medium text-app-text md:flex-none">
                    Designer
                  </span>
                  {/* Direct shortcut to the manual config form. The whole
                      row is a <Link> (anchor), so this MUST be a button —
                      nesting <a> inside <a> trips React's hydration check
                      ("<a> cannot be a descendant of <a>"). We
                      stopPropagation + router.visit to navigate. */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      router.visit(`/skills/${s.id}/edit`)
                    }}
                    className="flex-1 rounded-control border border-border px-3 py-1.5 text-center text-xs font-medium text-app-muted transition hover:bg-white/5 hover:text-app-text md:flex-none"
                  >
                    Manual edit
                  </button>
                  {s.status !== "archived" && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        if (!confirm("Archive this skill? It will stop running and be removed from attached agents.")) return
                        router.post(`/skills/${s.id}/archive`, {}, { preserveScroll: true })
                      }}
                      className="flex-1 rounded-control border border-status-error/40 px-3 py-1.5 text-center text-xs font-medium text-status-error transition hover:bg-status-error/10 md:flex-none"
                    >
                      Archive
                    </button>
                  )}
                </span>
              )}
              </div>
            </>
          )

          if (isGlobalMode) {
            return (
              <div key={s.id} className={rowClass}>
                {innerCells}
              </div>
            )
          }

          return (
            <Link key={s.id} href={`/skills/${s.id}/test`} className={rowClass}>
              {innerCells}
            </Link>
          )
        })}
      </div>
    </div>
  )
}
